import type { ConnectionPhase } from '@parsleyai/ui'
import { disableNetwork, enableNetwork } from 'firebase/firestore'
import { db } from '@/services/firebase/config'

// Every live subscription shares ONE Firestore WebChannel, and Firestore binds its auth + App Check
// credentials at stream-open time — so after a device suspend, calling `onSnapshot` again only re-adds a
// target to the same dead stream. Recovery has to happen at the CLIENT level, which this module owns.

/** Attempts 1..N only re-open the listeners — enough for an ordinary transient blip. */
const RELISTEN_ATTEMPTS = 2
/** The next attempts tear the WebChannel down and rebuild it, re-minting credentials. */
const REBUILD_ATTEMPTS = 3
/** Past those the connection is not coming back on its own — stop, and offer a reload. */

const BASE_BACKOFF_MS = 1000
const MAX_BACKOFF_MS = 30_000
/** Listeners fail in a burst (they share one stream) — coalesce them into a single attempt. */
const FAILURE_DEBOUNCE_MS = 150
const RESTORED_HOLD_MS = 1900
/** How often we sample the wall clock to notice the device was suspended. */
const HEARTBEAT_MS = 30_000
/** A hidden/frozen stretch at least this long gets a pre-emptive transport rebuild on wake. */
const RESUME_THRESHOLD_MS = 5 * 60_000

export interface ConnectionState {
  phase: ConnectionPhase
  /** Bumped when every listener must re-open. `useLiveSubscription` watches this. */
  generation: number
}

type Rung = 'relisten' | 'rebuild' | 'giveup'

function rungFor(attemptNumber: number): Rung {
  if (attemptNumber <= RELISTEN_ATTEMPTS) return 'relisten'
  if (attemptNumber <= RELISTEN_ATTEMPTS + REBUILD_ATTEMPTS) return 'rebuild'
  return 'giveup'
}

let state: ConnectionState = { phase: 'live', generation: 0 }
const subscribers = new Set<() => void>()

let attempt = 0
let activeListeners = 0
let resyncing = false
// A generation is poisoned the moment any listener reports a failure in it, so one slice's
// healthy snapshot can't disarm the ladder while a sibling is still broken.
let generationFailed = false

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let ladderTimer: ReturnType<typeof setTimeout> | null = null
let restoredTimer: ReturnType<typeof setTimeout> | null = null
let heartbeat: ReturnType<typeof setInterval> | null = null
let hiddenAt: number | null = null
let lastHeartbeatAt = 0

function emit(next: Partial<ConnectionState>): void {
  const merged = { ...state, ...next }
  if (merged.phase === state.phase && merged.generation === state.generation) return
  state = merged
  for (const notify of subscribers) notify()
}

function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer) clearTimeout(timer)
  return null
}

function clearLadder(): void {
  debounceTimer = clearTimer(debounceTimer)
  ladderTimer = clearTimer(ladderTimer)
}

export function subscribeToConnection(onChange: () => void): () => void {
  subscribers.add(onChange)
  return () => {
    subscribers.delete(onChange)
  }
}

export function getConnectionState(): ConnectionState {
  return state
}

// --- the ladder ---------------------------------------------------------------------------

function beginAttempt(options: { immediate?: boolean; startAtRebuild?: boolean } = {}): void {
  if (options.startAtRebuild && attempt < RELISTEN_ATTEMPTS) attempt = RELISTEN_ATTEMPTS
  attempt += 1

  const rung = rungFor(attempt)
  if (rung === 'giveup') {
    console.error('[connection] recovery exhausted — a reload is the only way back')
    emit({ phase: 'unrecoverable' })
    return
  }

  emit({ phase: rung === 'rebuild' ? 'stalled' : 'reconnecting' })
  const delay = options.immediate ? 0 : Math.min(BASE_BACKOFF_MS * 2 ** (attempt - 1), MAX_BACKOFF_MS)
  ladderTimer = setTimeout(runAttempt, delay)
}

async function runAttempt(): Promise<void> {
  ladderTimer = null
  if (rungFor(attempt) === 'rebuild') await rebuildTransport()
  // Re-open every listener. Anything still broken re-poisons the generation and escalates.
  generationFailed = false
  emit({ generation: state.generation + 1 })
}

async function rebuildTransport(): Promise<void> {
  try {
    // The only lever that discards the dead WebChannel and re-mints the auth + App Check
    // tokens bound to it. `enableNetwork` re-attaches every active target on the new stream.
    await disableNetwork(db)
    await enableNetwork(db)
  } catch (error) {
    console.error('[connection] transport rebuild failed', error)
  }
}

/** A listener's `onSnapshot` error callback fired. Firestore never re-attaches that listener itself. */
export function reportFailure(label: string): void {
  if (activeListeners === 0) return
  generationFailed = true
  // Once we've given up, keep quiet rather than restarting the ladder on every stray error.
  if (state.phase === 'unrecoverable') return
  if (debounceTimer || ladderTimer) return
  console.warn(`[connection] ${label} dropped — starting recovery`)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    beginAttempt()
  }, FAILURE_DEBOUNCE_MS)
}

/**
 * A listener received **server** data. Callers must not report cache replays: Firestore serves a
 * cached snapshot the instant a listener re-opens, and counting that as recovery is exactly what
 * pinned the old per-listener backoff at its first step forever.
 */
export function reportHealthy(): void {
  if (generationFailed) return
  attempt = 0
  clearLadder()
  if (state.phase === 'live') return
  emit({ phase: 'restored' })
  restoredTimer = clearTimer(restoredTimer)
  restoredTimer = setTimeout(() => emit({ phase: 'live' }), RESTORED_HOLD_MS)
}

/** The banner's "Try now". Re-listening has demonstrably already failed, so go straight to the transport. */
export function retryConnection(): void {
  if (activeListeners === 0) return
  clearLadder()
  restoredTimer = clearTimer(restoredTimer)
  beginAttempt({ immediate: true, startAtRebuild: true })
}

/** The banner's "Reload" — the guaranteed fix, since a fresh client discards all stale target state. */
export function reloadApp(): void {
  window.location.reload()
}

// --- resume detection ---------------------------------------------------------------------

// A suspended device's channel is dead on wake even though nothing has errored yet. Rebuilding
// pre-emptively beats waiting for eight listeners to time out — and because it runs silently,
// a healthy wake never flashes a banner. If it fails, the listeners report and the ladder
// takes over with its visible phases.
function onResume(reason: string): void {
  if (activeListeners === 0 || resyncing) return
  console.info(`[connection] resume detected (${reason}) — resyncing transport`)
  clearLadder()
  attempt = 0
  void resyncTransport()
}

async function resyncTransport(): Promise<void> {
  resyncing = true
  try {
    await rebuildTransport()
    generationFailed = false
    emit({ generation: state.generation + 1 })
  } finally {
    resyncing = false
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now()
    return
  }
  const away = hiddenAt === null ? 0 : Date.now() - hiddenAt
  hiddenAt = null
  if (away >= RESUME_THRESHOLD_MS) onResume(`visible after ${Math.round(away / 1000)}s hidden`)
}

function onHeartbeat(): void {
  const now = Date.now()
  const elapsed = now - lastHeartbeatAt
  lastHeartbeatAt = now
  // The detector that actually matters for a sleeping laptop: timers freeze with the device, so
  // a jump far beyond the interval means we were suspended. `visibilitychange` is not reliable
  // here — a lid close doesn't always hide the tab.
  if (elapsed >= RESUME_THRESHOLD_MS) onResume(`clock jumped ${Math.round(elapsed / 1000)}s`)
}

function onOnline(): void {
  onResume('network online')
}

function installResumeDetectors(): void {
  if (typeof document === 'undefined') return
  lastHeartbeatAt = Date.now()
  heartbeat = setInterval(onHeartbeat, HEARTBEAT_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onOnline)
}

function removeResumeDetectors(): void {
  if (typeof document === 'undefined') return
  if (heartbeat) clearInterval(heartbeat)
  heartbeat = null
  hiddenAt = null
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('online', onOnline)
}

/**
 * Every live listener registers while it's mounted. The count gates recovery (nothing to
 * recover with zero listeners) and owns the resume detectors' lifecycle.
 */
export function registerConnectionListener(): () => void {
  activeListeners += 1
  if (activeListeners === 1) installResumeDetectors()
  return () => {
    activeListeners -= 1
    if (activeListeners === 0) {
      removeResumeDetectors()
      resetConnectionRecovery()
    }
  }
}

/** Back to a clean slate (sign-out, or the last listener unmounting). `generation` stays monotonic. */
export function resetConnectionRecovery(): void {
  clearLadder()
  restoredTimer = clearTimer(restoredTimer)
  attempt = 0
  generationFailed = false
  emit({ phase: 'live' })
}
